import { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import SelfieCapture from './SelfieCapture';
import { getStateByCode, DEFAULT_STATE } from '../utils/states';

export default function IdentityVerification({ courseId, courseState, user, selfieRequired, onVerified, onCancel }) {
  // Look up state config from the course's state code (e.g., 'AZ', 'KS').
  // Falls back to Arizona for legacy courses that don't have a state set.
  const stateInfo = getStateByCode(courseState) || DEFAULT_STATE;
  const [licenseNumber, setLicenseNumber] = useState(user?.licenseNumber || '');
  const [signature, setSignature] = useState('');
  // Kansas: KREC credits a completion only if the name matches its records exactly, so
  // ask for the name as printed on the Kansas license (KREC Educators Guidebook).
  const isKansas = stateInfo.code === 'KS';
  const [ksFirstName, setKsFirstName] = useState(user?.firstName || '');
  const [ksLastName, setKsLastName] = useState(user?.lastName || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSelfie, setShowSelfie] = useState(false);
  const [selfieUploading, setSelfieUploading] = useState(false);

  const today = new Date().toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!licenseNumber.trim()) { setError('Please enter your license number.'); return; }
    if (isKansas && licenseNumber.replace(/\D/g, '').length !== 8) { setError('Kansas license numbers are 8 digits (leave off SP or BR).'); return; }
    if (isKansas && (!ksFirstName.trim() || !ksLastName.trim())) { setError('Please enter your first and last name exactly as they appear on your Kansas license.'); return; }
    if (!signature.trim()) { setError('Please type your electronic signature.'); return; }

    setSaving(true);
    setError('');
    try {
      const res = await api.post(`/verification/${courseId}`, { licenseNumber: licenseNumber.trim(), signature: signature.trim(), ...(isKansas && { ksFirstName: ksFirstName.trim(), ksLastName: ksLastName.trim() }) });
      if (res.data.selfieRequired) {
        setShowSelfie(true);
        setSaving(false);
      } else {
        onVerified();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Verification failed. Please try again.');
      setSaving(false);
    }
  };

  const handleSelfieCapture = async (imageData) => {
    setSelfieUploading(true);
    try {
      await api.post('/verification/selfie', { imageData });
      onVerified();
    } catch (err) {
      setError('Failed to upload photo. Please try again.');
      setSelfieUploading(false);
    }
  };

  if (showSelfie) {
    return (
      <div style={styles.root}>
        <div style={styles.card}>
          {selfieUploading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div className="spinner" style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: '#b39e89', margin: '0 auto 16px' }} />
              <p style={{ color: 'rgba(255,255,255,0.7)' }}>Uploading photo...</p>
            </div>
          ) : (
            <SelfieCapture onCapture={handleSelfieCapture} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>Confirm Your Identity:</h2>
          <Link to="/my-profile" style={styles.updateLink}>Update my contact information</Link>
        </div>

        <div style={styles.userInfo}>
          <div style={styles.userName}>{user?.firstName} {user?.lastName}</div>
          <div style={styles.userEmail}>{user?.email}</div>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.fieldRow}>
            <label style={styles.fieldLabel}>Enter your {stateInfo.name} license number:</label>
            <input
              style={styles.input}
              placeholder="Type here..."
              value={licenseNumber}
              onChange={e => setLicenseNumber(e.target.value)}
            />
            {stateInfo.licenseLookupUrl && (
              <a
                href={stateInfo.licenseLookupUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.lookupLink}
              >
                Look it up online
              </a>
            )}
          </div>

          {isKansas && (
            <div style={styles.fieldRow}>
              <label style={styles.fieldLabel}>Your name exactly as it appears on your Kansas license:</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input style={{ ...styles.input, flex: '1 1 140px' }} placeholder="First name" aria-label="First name as on your Kansas license"
                  value={ksFirstName} onChange={e => setKsFirstName(e.target.value)} />
                <input style={{ ...styles.input, flex: '1 1 140px' }} placeholder="Last name" aria-label="Last name as on your Kansas license"
                  value={ksLastName} onChange={e => setKsLastName(e.target.value)} />
              </div>
              <span style={styles.dateLabel}>
                We report your completion to KREC under this name and your 8-digit license number, so they must match your license — no nicknames, middle initials or suffixes like Jr.
              </span>
            </div>
          )}

          {/* State-specific compliance notice — currently only Arizona has the
              selfie-selection requirement (ADRE Feb 2024). Other states get
              a clean form with no notice. */}
          {stateInfo.code === 'AZ' && (
            <div style={styles.notice}>
              <h3 style={styles.noticeTitle}>NOTICE TO ARIZONA STUDENTS:</h3>
              <p style={styles.noticeText}>
                Beginning Feb 2024, the Arizona Department of Real Estate now requires enhanced verification
                of students. To meet this new requirement, we will be randomly selecting students to take a
                live selfie during their class to help confirm their identity. Thanks for your cooperation.
              </p>
            </div>
          )}

          <div style={styles.sigRow}>
            <label style={styles.fieldLabel}>Type your electronic signature here</label>
            <input
              style={styles.input}
              placeholder="Type here..."
              value={signature}
              onChange={e => setSignature(e.target.value)}
            />
            <span style={styles.dateLabel}>Date: {today}</span>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.buttons}>
            <button type="submit" disabled={saving} style={styles.beginBtn}>
              {saving ? 'Verifying...' : 'Begin the Course'}
            </button>
            <button type="button" onClick={onCancel} style={styles.cancelBtn}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  root: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100%', padding: 40,
  },
  card: {
    width: '100%', maxWidth: 720,
    background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(255,255,255,0.1)',
    borderRadius: 16, padding: '36px 40px',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: 20, flexWrap: 'wrap', gap: 12,
  },
  title: { fontFamily: 'var(--font-display)', fontSize: 24, color: 'white', margin: 0 },
  updateLink: { fontSize: 13, color: '#b39e89', textDecoration: 'none', fontWeight: 600 },
  userInfo: { marginBottom: 24 },
  userName: { fontSize: 16, color: 'rgba(255,255,255,0.9)', fontWeight: 600 },
  userEmail: { fontSize: 14, color: 'rgba(255,255,255,0.6)' },
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  fieldLabel: { fontSize: 14, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' },
  input: {
    flex: 1, minWidth: 180, padding: '10px 14px', borderRadius: 8,
    border: '1.5px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)',
    color: 'white', fontSize: 14, outline: 'none',
  },
  lookupLink: { fontSize: 13, color: '#b39e89', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' },
  notice: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12, padding: '18px 22px',
  },
  noticeTitle: { fontSize: 16, fontWeight: 800, color: 'white', marginBottom: 8 },
  noticeText: { fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: 0 },
  sigRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  dateLabel: { fontSize: 14, color: 'rgba(255,255,255,0.7)', fontWeight: 600, whiteSpace: 'nowrap' },
  errorBox: {
    background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 8, padding: '10px 16px', fontSize: 14, color: '#ef4444',
  },
  buttons: { display: 'flex', gap: 12, marginTop: 8 },
  beginBtn: {
    background: '#b39e89', color: 'white', border: 'none', borderRadius: 999,
    padding: '12px 32px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  cancelBtn: {
    background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
    padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
};
